import { ChevronRight } from "@mui/icons-material";
import cn from "classnames";
import Link from "next/link";
import { type NextRouter, useRouter } from "next/router";
import { type FC, type MouseEvent as ReactMouseEvent, useEffect, useState } from "react";

import {
	type DocumentPageMetadata,
	type PageMetadata,
	type PublishSettings,
	makeUrlPrefix,
} from "@moment/api-collab/api-types";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/ui/Collapsible";
import {
	SidebarMenuAction,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
} from "~/ui/Sidebar";
import { invokePostMessage, isRunningInIframe } from "~/utils/iframe";

import { type TreeItems } from "../../navigation-pane/types";

interface RouteProps {
	workspaceName: string;
	documentId: string;
	pageSlug: string;
}

async function handleAnchorTagClick(
	e: ReactMouseEvent<HTMLAnchorElement, MouseEvent>,
	route: RouteProps,
	router: NextRouter
) {
	if (!isRunningInIframe()) {
		return;
	}

	// The href for the anchor tag when running inside an iframe
	// has the parent frame's URL, so we don't want to let the
	// default click behavior to go through.
	e.preventDefault();
	e.stopPropagation();

	// Instead, perform a client-side routing manually.
	await router.push(
		`${makeUrlPrefix(route.workspaceName, route.documentId, undefined)}/${route.pageSlug}`
	);
}

/**
 * This horrible component exists because `SidebarMenuSub` is a different thing than `SidebarMenu`,
 * and therefore must be the root of recursion. Ideally, we would recurse in `SidebarMenuItemTree`.
 */
const RecursiveSubMenu: FC<{
	workspaceName: string;
	documentId: string;
	activePage: DocumentPageMetadata | null;
	pageTreeChildren: TreeItems<PageMetadata>;
	urlPrefix: string;
}> = ({ workspaceName, documentId, activePage, pageTreeChildren, urlPrefix }) => {
	const router = useRouter();

	return pageTreeChildren?.length ? (
		<>
			<CollapsibleTrigger asChild>
				<SidebarMenuAction className="data-[state=open]:rotate-90">
					<ChevronRight />
					<span className="sr-only">Toggle</span>
				</SidebarMenuAction>
			</CollapsibleTrigger>
			<CollapsibleContent>
				<SidebarMenuSub>
					{pageTreeChildren?.map((subItem) => (
						<Collapsible key={subItem.id} asChild defaultOpen>
							<SidebarMenuSubItem key={subItem.data.title}>
								<SidebarMenuSubButton
									isActive={subItem.id === activePage?.id}
									asChild
									className={cn({ "font-bold ": subItem.id === activePage?.id })}
								>
									{isRunningInIframe() ? (
										<a
											href={`${urlPrefix}/${subItem.data.slug}`}
											onClick={async (e) =>
												await handleAnchorTagClick(
													e,
													{
														workspaceName,
														documentId,
														pageSlug: subItem.data.slug,
													},
													router
												)
											}
										>
											{subItem.data.title}
										</a>
									) : (
										<Link href={`${urlPrefix}/${subItem.data.slug}`}>
											<span>{subItem.data.title}</span>
										</Link>
									)}
								</SidebarMenuSubButton>
								{subItem.children?.length ? (
									<RecursiveSubMenu
										workspaceName={workspaceName}
										documentId={documentId}
										activePage={activePage}
										pageTreeChildren={subItem.children}
										urlPrefix={urlPrefix}
									/>
								) : null}
							</SidebarMenuSubItem>
						</Collapsible>
					))}
				</SidebarMenuSub>
			</CollapsibleContent>
		</>
	) : null;
};

export const SidebarMenuItemTree: FC<{
	workspaceName: string;
	documentId: string;
	activePage: DocumentPageMetadata | null;
	pageTree: TreeItems<PageMetadata>;
	publishSettings: PublishSettings | undefined;
}> = ({ workspaceName, documentId, activePage, pageTree, publishSettings }) => {
	const [urlPrefix, setUrlPrefix] = useState(
		makeUrlPrefix(workspaceName, documentId, publishSettings)
	);
	const router = useRouter();
	useEffect(() => {
		if (!isRunningInIframe()) {
			return;
		}

		invokePostMessage<string>("ping")
			.then((parentOrigin?: string) => {
				if (parentOrigin && urlPrefix.startsWith("/")) {
					setUrlPrefix(
						`${parentOrigin}${makeUrlPrefix(workspaceName, documentId, undefined)}`
					);
				}
			})
			.catch((err) => console.error("Error sending ping request to the parent frame", err));
	}, [router.query, setUrlPrefix, workspaceName, documentId, urlPrefix]);

	return (
		<div>
			{pageTree.map((page) => {
				const isIndex = page.data.slug === "index";
				const href = isIndex ? urlPrefix : `${urlPrefix}/${page.data.slug}`;

				// LAME BUT IMPORTANT:
				//
				// 1. Cause the Next page router to behave well with custom
				// domains. If we are routing, say, `moment.dev/docs`, then the index page will be
				// `/docs`. If you use a `Link`, Next page router will try to route to a named route
				// called `/docs`. We don't want that, we want to route to the index page.
				//
				//
				// 2. For iframes, we will always use anchor tags with an href that
				// uses the parent frame's URL, so the links are shareable and attribute
				// to the correct domain. But on the client-side, we'll intercept the click
				// and route to the right page ourselves.
				const pageLink =
					isIndex || isRunningInIframe() ? (
						<a
							href={href}
							onClick={async (e) =>
								await handleAnchorTagClick(
									e,
									{ workspaceName, documentId, pageSlug: page.data.slug },
									router
								)
							}
						>
							{page.data.title}
						</a>
					) : (
						<Link href={href}>{page.data.title}</Link>
					);

				return (
					<Collapsible
						key={page.id}
						asChild
						defaultOpen
						//defaultOpen={item.isActive}
					>
						<SidebarMenuItem>
							<SidebarMenuButton
								isActive={page.id === activePage?.id}
								asChild
								className={cn({ "font-bold ": page.id === activePage?.id })}
							>
								{pageLink}
							</SidebarMenuButton>
							<RecursiveSubMenu
								workspaceName={workspaceName}
								documentId={documentId}
								activePage={activePage}
								pageTreeChildren={page.children}
								urlPrefix={urlPrefix}
							/>
						</SidebarMenuItem>
					</Collapsible>
				);
			})}
		</div>
	);
};
